/**
 * Multi-Part Search v4 — CSV + XLSX Upload Support
 * =======================================================
 * Works in two contexts:
 *   1. Homepage section  (#multi-part-search-section)
 *   2. Search page       (#Search-In-Template)
 *
 * Input methods:
 *   - Text input : comma / newline / tab / space / semicolon / pipe / "OR"
 *   - CSV upload : client-side parsing (drag-and-drop or file picker)
 *   - XLSX upload: client-side parsing via SheetJS (drag-and-drop or file picker)
 *     Detects columns named: sku, part number, part_number, partnumber,
 *     part no, part_no, partno, part#, part #
 *     Falls back to first column for single-column CSVs.
 */
(function () {
  'use strict';

  // ── Configuration ──
  var API_ENDPOINT = '/apps/multi-part-search/multi-part-search';
  var PDF_API_ENDPOINT = '/apps/multi-part-search/upload-pdf';
  var MAX_PARTS = 50;
  var MAX_CSV_SIZE = 1024 * 1024;       // 1 MB
  var MAX_PDF_SIZE = 5 * 1024 * 1024;   // 5 MB
  var ACCEPTED_HEADERS = [
    'sku', 'part number', 'part_number', 'partnumber',
    'part no', 'part_no', 'partno', 'part#', 'part #'
  ];
  var ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.pdf'];
  var MAX_XLSX_SIZE = 5 * 1024 * 1024;   // 5 MB
  var PDF_MAGIC = '%PDF';

  // ── State ──
  var isHomepage = false;
  var searchForm = null;
  var searchInput = null;
  var gridContainer = null;
  var loadingEl = null;
  var loadingText = null;
  var summaryEl = null;
  var resultsEl = null;
  var defaultResultsSection = null;

  // File upload elements
  var fileInput = null;
  var dropzone = null;
  var fileInfoEl = null;
  var filenameEl = null;
  var removeBtn = null;
  var partCountEl = null;
  var pendingFile = null;      // the selected File object
  var pendingFileType = null;  // 'csv' | 'pdf'

  // ═══════════════════════════════════════════════════════════════════
  //  PART NUMBER CLEANING
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Strip trailing condition-code suffix (e.g. "=E9", "=9B", "=K3")
   * commonly found in aviation part data exports.
   */
  var SUFFIX_PATTERN = /=[A-Za-z0-9]{1,3}$/;

  function cleanPartNumber(val) {
    return val.replace(SUFFIX_PATTERN, '');
  }

  // ═══════════════════════════════════════════════════════════════════
  //  FILE TYPE HELPERS
  // ═══════════════════════════════════════════════════════════════════

  function getFileExtension(filename) {
    var dot = filename.lastIndexOf('.');
    return dot !== -1 ? filename.slice(dot).toLowerCase() : '';
  }

  function isAcceptedFile(filename) {
    var ext = getFileExtension(filename);
    return ACCEPTED_EXTENSIONS.indexOf(ext) !== -1;
  }

  function isPDF(filename) {
    return getFileExtension(filename) === '.pdf';
  }

  function isCSV(filename) {
    return getFileExtension(filename) === '.csv';
  }

  function isXLSX(filename) {
    return getFileExtension(filename) === '.xlsx';
  }

  /**
   * Validate PDF magic bytes from an ArrayBuffer.
   * Returns true if the first 4 bytes are "%PDF".
   */
  function validatePDFMagic(arrayBuffer) {
    if (arrayBuffer.byteLength < 4) return false;
    var bytes = new Uint8Array(arrayBuffer, 0, 4);
    var header = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
    return header === PDF_MAGIC;
  }

  /**
   * Convert an ArrayBuffer to a base64 string (browser-native, lossless).
   */
  function arrayBufferToBase64(buffer) {
    var bytes = new Uint8Array(buffer);
    var binary = '';
    for (var i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  CSV PARSING  (unchanged from v4)
  // ═══════════════════════════════════════════════════════════════════

  function parseCSVLine(line) {
    var result = [];
    var current = '';
    var inQuotes = false;

    for (var i = 0; i < line.length; i++) {
      var ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          result.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
    }
    result.push(current);
    return result;
  }

  function parseCSV(text) {
    if (text.charCodeAt(0) === 0xFEFF) {
      text = text.slice(1);
    }

    var lines = text.split(/\r?\n/).filter(function (l) { return l.trim().length > 0; });
    if (lines.length === 0) return { error: 'CSV file is empty.' };
    if (lines.length === 1) return { error: 'CSV file has only a header row and no data.' };

    var headers = parseCSVLine(lines[0]).map(function (h) { return h.trim(); });
    var headersLower = headers.map(function (h) { return h.toLowerCase(); });

    var colIndex = -1;
    var colName = '';
    for (var i = 0; i < headersLower.length; i++) {
      if (ACCEPTED_HEADERS.indexOf(headersLower[i]) !== -1) {
        colIndex = i;
        colName = headers[i];
        break;
      }
    }

    if (colIndex === -1 && headers.length === 1) {
      colIndex = 0;
      colName = headers[0];
    }

    if (colIndex === -1) {
      return {
        error: 'Could not find a column named "SKU" or "Part Number" in the CSV. ' +
               'Found columns: ' + headers.join(', ')
      };
    }

    var parts = [];
    for (var j = 1; j < lines.length; j++) {
      var row = parseCSVLine(lines[j]);
      if (row.length > colIndex) {
        var val = cleanPartNumber(row[colIndex].trim());
        if (val.length > 0) {
          parts.push(val);
        }
      }
    }

    if (parts.length === 0) {
      return { error: 'No part numbers found in the "' + colName + '" column.' };
    }

    return { parts: parts, column: colName };
  }

  // ═══════════════════════════════════════════════════════════════════
  //  XLSX PARSING  (requires SheetJS loaded externally)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Parse an XLSX file from an ArrayBuffer.
   * Uses the same column-detection logic as CSV.
   * Requires the XLSX global (SheetJS) to be loaded.
   */
  function parseXLSX(arrayBuffer) {
    if (typeof XLSX === 'undefined') {
      return { error: 'Excel parser not loaded. Please refresh the page and try again.' };
    }
    try {
      var workbook = XLSX.read(arrayBuffer, { type: 'array' });
      var sheetName = workbook.SheetNames[0];
      if (!sheetName) return { error: 'The Excel file has no sheets.' };
      var sheet = workbook.Sheets[sheetName];
      var rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

      if (rows.length === 0) return { error: 'The Excel file is empty.' };
      if (rows.length === 1) return { error: 'The Excel file has only a header row and no data.' };

      var headers = rows[0].map(function (h) { return String(h).trim(); });
      var headersLower = headers.map(function (h) { return h.toLowerCase(); });

      var colIndex = -1;
      var colName = '';
      for (var i = 0; i < headersLower.length; i++) {
        if (ACCEPTED_HEADERS.indexOf(headersLower[i]) !== -1) {
          colIndex = i;
          colName = headers[i];
          break;
        }
      }

      if (colIndex === -1 && headers.length === 1) {
        colIndex = 0;
        colName = headers[0];
      }

      if (colIndex === -1) {
        return {
          error: 'Could not find a column named "SKU" or "Part Number" in the Excel file. ' +
                 'Found columns: ' + headers.join(', ')
        };
      }

      var parts = [];
      for (var j = 1; j < rows.length; j++) {
        if (rows[j].length > colIndex) {
          var val = cleanPartNumber(String(rows[j][colIndex]).trim());
          if (val.length > 0) {
            parts.push(val);
          }
        }
      }

      if (parts.length === 0) {
        return { error: 'No part numbers found in the "' + colName + '" column.' };
      }

      return { parts: parts, column: colName };
    } catch (e) {
      return { error: 'Failed to parse the Excel file: ' + e.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  PART-NUMBER DETECTION
  //  Delimiters: comma, newline, tab, space, semicolon, pipe, "OR"
  // ═══════════════════════════════════════════════════════════════════

  function isMultiPartQuery(input) {
    var trimmed = input.trim();
    if (!trimmed) return false;
    if (/[,\n\t;|]/.test(trimmed)) return true;
    if (/\bOR\b/i.test(trimmed)) return true;
    if (/\s+/.test(trimmed)) return true;
    return false;
  }

  function parsePartNumbers(input) {
    return input
      .replace(/\bOR\b/gi, ',')
      .split(/[,\n\t;|\s]+/)
      .map(function (s) { return cleanPartNumber(s.trim()); })
      .filter(function (s) { return s.length > 0; });
  }

  // ═══════════════════════════════════════════════════════════════════
  //  FORM INTERCEPTION
  // ═══════════════════════════════════════════════════════════════════

  function interceptForm(e) {
    // ── File upload takes priority ──
    if (pendingFile) {
      e.preventDefault();
      if (pendingFileType === 'pdf') {
        handlePDFUpload();
      } else if (pendingFileType === 'xlsx') {
        handleXLSXSearch();
      } else {
        handleCSVSearch();
      }
      return;
    }

    var rawInput = searchInput ? searchInput.value.trim() : '';

    if (isHomepage) {
      e.preventDefault();
      if (!rawInput) return;

      var partNumbers = parsePartNumbers(rawInput);
      if (partNumbers.length === 0) return;

      if (partNumbers.length > MAX_PARTS) {
        renderTruncationWarning(partNumbers.length, MAX_PARTS);
        partNumbers = partNumbers.slice(0, MAX_PARTS);
      }

      searchMultipleParts(partNumbers);
    } else {
      if (!rawInput || !isMultiPartQuery(rawInput)) {
        return;
      }

      e.preventDefault();

      var partNumbers = parsePartNumbers(rawInput);
      if (partNumbers.length <= 1) {
        searchForm.submit();
        return;
      }

      if (partNumbers.length > MAX_PARTS) {
        renderTruncationWarning(partNumbers.length, MAX_PARTS);
        partNumbers = partNumbers.slice(0, MAX_PARTS);
      }

      searchMultipleParts(partNumbers);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  CSV SEARCH  (client-side parsing, same as v4)
  // ═══════════════════════════════════════════════════════════════════

  function handleCSVSearch() {
    var file = pendingFile;
    if (!file) return;

    if (file.size > MAX_CSV_SIZE) {
      renderError('CSV file is too large. Maximum allowed size is 1 MB.');
      return;
    }

    showLoading('Parsing CSV…');
    clearResults();
    hideDefaultResults();

    var reader = new FileReader();
    reader.onload = function (e) {
      var result = parseCSV(e.target.result);

      if (result.error) {
        hideLoading();
        renderError(result.error);
        return;
      }

      var parts = result.parts;

      if (parts.length > MAX_PARTS) {
        renderTruncationWarning(parts.length, MAX_PARTS);
        parts = parts.slice(0, MAX_PARTS);
      }

      // Populate textarea with extracted parts so user can see them
      if (searchInput) {
        searchInput.value = parts.join('\n');
      }
      clearPendingFile();

      setLoadingText('Searching parts…');
      searchMultipleParts(parts);
    };
    reader.onerror = function () {
      hideLoading();
      renderError('Failed to read the CSV file. Please try again.');
    };
    reader.readAsText(file);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  XLSX SEARCH  (client-side parsing via SheetJS)
  // ═══════════════════════════════════════════════════════════════════

  function handleXLSXSearch() {
    var file = pendingFile;
    if (!file) return;

    if (file.size > MAX_XLSX_SIZE) {
      renderError('Excel file is too large. Maximum allowed size is 5 MB.');
      return;
    }

    if (typeof XLSX === 'undefined') {
      renderError('Excel parser not loaded. Please refresh the page and try again.');
      return;
    }

    showLoading('Parsing Excel file…');
    clearResults();
    hideDefaultResults();

    var reader = new FileReader();
    reader.onload = function (e) {
      var result = parseXLSX(e.target.result);

      if (result.error) {
        hideLoading();
        renderError(result.error);
        return;
      }

      var parts = result.parts;

      if (parts.length > MAX_PARTS) {
        renderTruncationWarning(parts.length, MAX_PARTS);
        parts = parts.slice(0, MAX_PARTS);
      }

      // Populate textarea with extracted parts so user can see them
      if (searchInput) {
        searchInput.value = parts.join('\n');
      }
      clearPendingFile();

      setLoadingText('Searching parts…');
      searchMultipleParts(parts);
    };
    reader.onerror = function () {
      hideLoading();
      renderError('Failed to read the Excel file. Please try again.');
    };
    reader.readAsArrayBuffer(file);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  PDF UPLOAD  (base64 → JSON → App Proxy → backend)
  // ═══════════════════════════════════════════════════════════════════

  function handlePDFUpload() {
    var file = pendingFile;
    if (!file) return;

    // ── Client-side validations ──
    if (file.size === 0) {
      renderError('The PDF file is empty (0 bytes). Please select a valid file.');
      return;
    }

    if (file.size > MAX_PDF_SIZE) {
      var sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      renderError(
        'PDF file is too large (' + sizeMB + ' MB). Maximum allowed size is ' +
        (MAX_PDF_SIZE / (1024 * 1024)) + ' MB.'
      );
      return;
    }

    showLoading('Reading PDF…');
    clearResults();
    hideDefaultResults();

    var reader = new FileReader();

    reader.onload = function (e) {
      var arrayBuffer = e.target.result;

      // Validate magic bytes — ensures it's actually a PDF
      if (!validatePDFMagic(arrayBuffer)) {
        hideLoading();
        renderError(
          'This file does not appear to be a valid PDF. ' +
          'Please upload a genuine PDF document.'
        );
        return;
      }

      // Convert to base64 (lossless)
      var base64;
      try {
        base64 = arrayBufferToBase64(arrayBuffer);
      } catch (err) {
        hideLoading();
        renderError('Failed to encode the PDF file. Please try a smaller file or contact support.');
        return;
      }

      // Verify round-trip integrity: decoded length must match original
      var decodedLength;
      try {
        decodedLength = atob(base64).length;
      } catch (err) {
        hideLoading();
        renderError('File encoding integrity check failed. Please try again.');
        return;
      }

      if (decodedLength !== arrayBuffer.byteLength) {
        hideLoading();
        renderError(
          'File integrity check failed: encoded size (' + decodedLength +
          ' bytes) does not match original (' + arrayBuffer.byteLength +
          ' bytes). Please try again.'
        );
        return;
      }

      setLoadingText('Uploading PDF to server…');
      sendPDFToBackend(base64, file.name, arrayBuffer.byteLength);
    };

    reader.onerror = function () {
      hideLoading();
      renderError('Failed to read the PDF file. The file may be corrupted or inaccessible.');
    };

    // Read as ArrayBuffer (not text) — preserves binary integrity
    reader.readAsArrayBuffer(file);
  }

  /**
   * Send the base64-encoded PDF to the backend via App Proxy.
   * The backend extracts part numbers and returns the same response shape.
   */
  async function sendPDFToBackend(base64Data, filename, originalSize) {
    try {
      setLoadingText('Server is extracting part numbers from PDF…');

      var payload = {
        file_base64: base64Data,
        filename: filename,
        file_type: 'pdf',
        original_size: originalSize
      };

      var response = await fetch(PDF_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // ── Handle HTTP errors ──
      if (!response.ok) {
        var status = response.status;
        var errorMsg;

        if (status === 413) {
          errorMsg = 'The PDF file is too large for the server to process. Please try a smaller file.';
        } else if (status === 415) {
          errorMsg = 'The server could not process this file type. Please ensure it is a valid PDF.';
        } else if (status === 422) {
          // Try to get server's error message
          try {
            var errData = await response.json();
            errorMsg = errData.error || errData.message || 'The server could not extract part numbers from this PDF.';
          } catch (_) {
            errorMsg = 'The server could not extract part numbers from this PDF.';
          }
        } else if (status === 504 || status === 408) {
          errorMsg = 'The server took too long to process the PDF. Please try a smaller file.';
        } else {
          errorMsg = 'PDF upload failed (HTTP ' + status + '). Please try again.';
        }

        hideLoading();
        renderError(errorMsg);
        return;
      }

      var data;
      try {
        data = await response.json();
      } catch (_) {
        hideLoading();
        renderError('Received an invalid response from the server. Please try again.');
        return;
      }

      // ── Handle application-level errors ──
      if (!data.success) {
        hideLoading();
        var serverError = data.error || data.message || 'Failed to process the PDF.';

        // Map known error codes to user-friendly messages
        if (data.error_code === 'PDF_NO_TEXT') {
          serverError = 'Could not extract text from this PDF. It may be a scanned document or image-based. Please use a text-based PDF or upload a CSV instead.';
        } else if (data.error_code === 'PDF_ENCRYPTED') {
          serverError = 'This PDF is password-protected. Please remove the password and try again, or upload a CSV instead.';
        } else if (data.error_code === 'PDF_CORRUPT') {
          serverError = 'This PDF appears to be corrupted and could not be read. Please try a different file.';
        } else if (data.error_code === 'NO_PARTS_FOUND') {
          serverError = 'No part numbers could be detected in this PDF. Please verify the document contains part numbers, or use the text input / CSV upload instead.';
        }

        renderError(serverError);
        return;
      }

      // ── Success — render results exactly like text/CSV flow ──
      var summary = data.summary;
      var foundProducts = data.found_products || [];
      var notFoundSkus = data.not_found_skus || [];

      // Populate textarea with all extracted parts so user can see them
      var allExtractedParts = [];
      foundProducts.forEach(function (p) { allExtractedParts.push(p.sku); });
      notFoundSkus.forEach(function (s) { allExtractedParts.push(s); });
      if (searchInput && allExtractedParts.length > 0) {
        searchInput.value = allExtractedParts.join('\n');
      }
      clearPendingFile();

      // Show truncation warning if backend reports it
      if (data.truncated && data.total_extracted) {
        renderTruncationWarning(data.total_extracted, MAX_PARTS);
      }

      renderSummary(summary, notFoundSkus);

      if (foundProducts.length === 0) {
        resultsEl.innerHTML =
          '<div class="title-wrapper center">' +
            '<h2 class="title title--primary">No products found for the part numbers extracted from the PDF.</h2>' +
          '</div>';
        resultsEl.style.display = 'block';
        hideLoading();
        return;
      }

      // Re-use Section Rendering API for product cards
      var matchedHandles = new Set(foundProducts.map(function (p) { return p.handle; }));
      var skus = foundProducts.map(function (p) { return p.sku; });
      var query = skus.join(' OR ');

      setLoadingText('Rendering product cards…');

      var sectionUrl =
        '/search?q=' + encodeURIComponent(query) +
        '&options%5Bprefix%5D=last' +
        '&type=product' +
        '&section_id=main-search';

      var sectionResponse = await fetch(sectionUrl);
      if (!sectionResponse.ok) {
        throw new Error('Failed to render product cards');
      }

      var html = await sectionResponse.text();
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var productGrid = doc.querySelector('#product-grid');

      if (!productGrid) {
        resultsEl.innerHTML =
          '<p>Products found but could not render cards.</p>';
        resultsEl.style.display = 'block';
        hideLoading();
        return;
      }

      var gridItems = productGrid.querySelectorAll('li.grid__item');
      gridItems.forEach(function (item) {
        var link = item.querySelector('a[href*="/products/"]');
        if (link) {
          var href = link.getAttribute('href');
          var match = href.match(/\/products\/([^?#\/]+)/);
          if (match && !matchedHandles.has(match[1])) {
            item.remove();
          }
        }
      });

      // Extract just the <ul> grid and inject it directly.
      // The resultsEl container already sits inside a page-width wrapper,
      // so we avoid double-nesting page-width which would add extra padding
      // and cause the grid to be narrower than the Featured Collection.
      var gridUl = productGrid.querySelector('ul.product-grid');
      if (gridUl) {
        // Add Featured Collection grid classes for consistent card spacing
        gridUl.classList.add('contains-card', 'contains-card--product', 'contains-card--standard');
        resultsEl.innerHTML = gridUl.outerHTML;
      } else {
        resultsEl.innerHTML = productGrid.innerHTML;
      }
      resultsEl.style.display = 'block';

    } catch (error) {
      console.error('PDF upload error:', error);
      renderError(
        'An unexpected error occurred while processing the PDF. ' +
        'Please try again or contact support.'
      );
    } finally {
      hideLoading();
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MULTI-PART SEARCH FLOW  (text + CSV, unchanged from v4)
  // ═══════════════════════════════════════════════════════════════════

  async function searchMultipleParts(partNumbers) {
    showLoading('Searching parts…');
    clearResults();
    hideDefaultResults();

    try {
      var apiResponse = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ part_numbers: partNumbers }),
      });

      if (!apiResponse.ok) {
        throw new Error('Search failed: ' + apiResponse.status);
      }

      var data = await apiResponse.json();

      if (!data.success) {
        throw new Error(data.message || 'Search failed');
      }

      var summary = data.summary;
      var foundProducts = data.found_products || [];
      var notFoundSkus = data.not_found_skus || [];

      renderSummary(summary, notFoundSkus);

      if (foundProducts.length === 0) {
        resultsEl.innerHTML =
          '<div class="title-wrapper center">' +
            '<h2 class="title title--primary">No products found for the given part numbers.</h2>' +
          '</div>';
        resultsEl.style.display = 'block';
        hideLoading();
        return;
      }

      var matchedHandles = new Set(foundProducts.map(function (p) { return p.handle; }));

      var skus = foundProducts.map(function (p) { return p.sku; });
      var query = skus.join(' OR ');

      var sectionUrl =
        '/search?q=' + encodeURIComponent(query) +
        '&options%5Bprefix%5D=last' +
        '&type=product' +
        '&section_id=main-search';

      var sectionResponse = await fetch(sectionUrl);
      if (!sectionResponse.ok) {
        throw new Error('Failed to render product cards');
      }

      var html = await sectionResponse.text();
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var productGrid = doc.querySelector('#product-grid');

      if (!productGrid) {
        resultsEl.innerHTML =
          '<p>Products found but could not render cards.</p>';
        resultsEl.style.display = 'block';
        hideLoading();
        return;
      }

      var gridItems = productGrid.querySelectorAll('li.grid__item');
      gridItems.forEach(function (item) {
        var link = item.querySelector('a[href*="/products/"]');
        if (link) {
          var href = link.getAttribute('href');
          var match = href.match(/\/products\/([^?#\/]+)/);
          if (match && !matchedHandles.has(match[1])) {
            item.remove();
          }
        }
      });

      // Extract just the <ul> grid — avoid wrapping in page-width
      // since resultsEl already sits inside a page-width container
      var gridUl = productGrid.querySelector('ul.product-grid');
      if (gridUl) {
        // Add Featured Collection grid classes for consistent card spacing
        gridUl.classList.add('contains-card', 'contains-card--product', 'contains-card--standard');
        resultsEl.innerHTML = gridUl.outerHTML;
      } else {
        resultsEl.innerHTML = productGrid.innerHTML;
      }
      resultsEl.style.display = 'block';

    } catch (error) {
      console.error('Multi-part search error:', error);
      renderError(error.message);
    } finally {
      hideLoading();
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  UI HELPERS
  // ═══════════════════════════════════════════════════════════════════

  function renderTruncationWarning(totalCount, limit) {
    if (!summaryEl) return;
    summaryEl.innerHTML =
      '<div style="background:#fff7e6;border:1px solid #ffd666;border-radius:12px;padding:14px 18px;margin-bottom:12px;">' +
        '<strong style="color:#8a5a00;">Note:</strong> ' +
        '<span style="color:#8a5a00;">You provided ' + totalCount + ' part numbers. ' +
        'Maximum bulk search can only be done on ' + limit + ' products, ' +
        'so the first ' + limit + ' parts are being searched.</span>' +
      '</div>';
    summaryEl.style.display = 'block';
  }

  function renderSummary(summary, notFoundSkus) {
    var html =
      '<div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;margin-bottom:16px;">' +
        '<p role="status" style="margin:0;">' +
          '<strong>' + summary.found + '</strong> of <strong>' + summary.unique_searched + '</strong> part numbers found' +
          (summary.not_found > 0
            ? '&nbsp;&middot;&nbsp;<span style="color:#b00020;">' + summary.not_found + ' not found</span>'
            : '') +
          (summary.duplicates_removed > 0
            ? '&nbsp;&middot;&nbsp;' + summary.duplicates_removed + ' duplicates removed'
            : '') +
        '</p>' +
        '<span>' + summary.found + ' results</span>' +
      '</div>';

    if (notFoundSkus && notFoundSkus.length > 0) {
      html +=
        '<div style="background:#fff7e6;border:1px solid #ffd666;border-radius:12px;padding:14px 18px;margin-bottom:20px;">' +
          '<strong style="color:#8a5a00;">Parts not found:</strong> ' +
          '<span style="color:#8a5a00;">' + escapeHtml(notFoundSkus.join(', ')) + '</span>' +
        '</div>';
    }

    // Preserve any existing truncation warning
    var existing = summaryEl.innerHTML;
    summaryEl.innerHTML = existing + html;
    summaryEl.style.display = 'block';
  }

  function renderError(message) {
    resultsEl.innerHTML =
      '<div style="background:#fee;border:1px solid #fcc;border-radius:12px;padding:18px;margin-top:16px;">' +
        '<strong style="color:#b00020;">Error:</strong>' +
        '<p style="color:#b00020;margin:8px 0 0;">' + escapeHtml(message) + '</p>' +
        '<p style="margin:8px 0 0;font-size:14px;color:#666;">Please try again or contact support.</p>' +
      '</div>';
    resultsEl.style.display = 'block';
  }

  function hideDefaultResults() {
    if (gridContainer) gridContainer.style.display = 'none';
    defaultResultsSection = document.querySelector('.template-search__header p[role="status"]');
    if (defaultResultsSection) defaultResultsSection.style.display = 'none';
  }

  function showLoading(text) {
    if (loadingEl) loadingEl.style.display = 'flex';
    setLoadingText(text || 'Searching parts…');
  }

  function setLoadingText(text) {
    if (loadingText) loadingText.textContent = text;
  }

  function hideLoading() {
    if (loadingEl) loadingEl.style.display = 'none';
  }

  function clearResults() {
    if (resultsEl) { resultsEl.innerHTML = ''; resultsEl.style.display = 'none'; }
    if (summaryEl) { summaryEl.innerHTML = ''; summaryEl.style.display = 'none'; }
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  FILE UPLOAD UI HANDLERS  (CSV + PDF)
  // ═══════════════════════════════════════════════════════════════════

  function initFileUploadHandlers() {
    // File picker change
    fileInput.addEventListener('change', function () {
      if (fileInput.files.length > 0) {
        onFileSelected(fileInput.files[0]);
      } else {
        clearPendingFile();
      }
    });

    // Remove button
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        clearPendingFile();
        fileInput.value = '';
        clearResults();
      });
    }

    // Dropzone click → open file picker
    if (dropzone) {
      dropzone.addEventListener('click', function () {
        fileInput.click();
      });

      dropzone.addEventListener('dragover', function (e) {
        e.preventDefault();
        dropzone.classList.add('mps-csv-dropzone--active');
      });

      dropzone.addEventListener('dragleave', function () {
        dropzone.classList.remove('mps-csv-dropzone--active');
      });

      dropzone.addEventListener('drop', function (e) {
        e.preventDefault();
        dropzone.classList.remove('mps-csv-dropzone--active');

        var files = e.dataTransfer.files;
        if (files.length === 0) return;

        var droppedFile = files[0];
        if (!isAcceptedFile(droppedFile.name)) {
          renderError('Unsupported file type. Please upload a CSV, Excel (.xlsx), or PDF file.');
          return;
        }

        onFileSelected(droppedFile);
      });
    }
  }

  /**
   * Handle a newly selected file (from picker or drag-drop).
   */
  function onFileSelected(file) {
    if (!file) return;

    var ext = getFileExtension(file.name);

    if (!isAcceptedFile(file.name)) {
      renderError('Unsupported file type "' + ext + '". Please upload a CSV, Excel (.xlsx), or PDF file.');
      return;
    }

    if (isCSV(file.name)) {
      if (file.size > MAX_CSV_SIZE) {
        renderError('CSV file is too large. Maximum allowed size is 1 MB.');
        return;
      }
      pendingFile = file;
      pendingFileType = 'csv';
      showFileInfo(file);
    } else if (isXLSX(file.name)) {
      if (file.size > MAX_XLSX_SIZE) {
        var sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        renderError(
          'Excel file is too large (' + sizeMB + ' MB). Maximum allowed size is ' +
          (MAX_XLSX_SIZE / (1024 * 1024)) + ' MB.'
        );
        return;
      }
      pendingFile = file;
      pendingFileType = 'xlsx';
      showFileInfo(file);
    } else if (isPDF(file.name)) {
      if (file.size === 0) {
        renderError('The PDF file is empty (0 bytes). Please select a valid file.');
        return;
      }
      if (file.size > MAX_PDF_SIZE) {
        var sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        renderError(
          'PDF file is too large (' + sizeMB + ' MB). Maximum allowed size is ' +
          (MAX_PDF_SIZE / (1024 * 1024)) + ' MB.'
        );
        return;
      }
      pendingFile = file;
      pendingFileType = 'pdf';
      showFileInfo(file);
    }
  }

  function showFileInfo(file) {
    if (!fileInfoEl || !filenameEl) return;

    var sizeKB = (file.size / 1024).toFixed(1);
    var sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    var sizeLabel = file.size > 1024 * 1024 ? sizeMB + ' MB' : sizeKB + ' KB';

    filenameEl.textContent = file.name + ' (' + sizeLabel + ')';

    if (partCountEl) {
      if (isPDF(file.name)) {
        // PDF: can't count parts client-side
        partCountEl.textContent = 'PDF — part numbers will be extracted by the server';
        partCountEl.style.color = '#555';
      } else if (isXLSX(file.name)) {
        // XLSX: count parts client-side via SheetJS
        partCountEl.textContent = 'Counting parts…';
        partCountEl.style.color = '#555';

        var reader = new FileReader();
        reader.onload = function (e) {
          var result = parseXLSX(e.target.result);
          if (result.parts) {
            var count = result.parts.length;
            partCountEl.textContent = count + ' part number' + (count !== 1 ? 's' : '') + ' detected';
            partCountEl.style.color = count > MAX_PARTS ? '#b00020' : '#1a7742';
          } else if (result.error) {
            partCountEl.textContent = result.error;
            partCountEl.style.color = '#b00020';
          }
        };
        reader.onerror = function () {
          partCountEl.textContent = 'Could not preview file';
          partCountEl.style.color = '#b00020';
        };
        reader.readAsArrayBuffer(file);
      } else {
        // CSV: count parts client-side
        partCountEl.textContent = 'Counting parts…';
        partCountEl.style.color = '#555';

        var reader = new FileReader();
        reader.onload = function (e) {
          var result = parseCSV(e.target.result);
          if (result.parts) {
            var count = result.parts.length;
            partCountEl.textContent = count + ' part number' + (count !== 1 ? 's' : '') + ' detected';
            partCountEl.style.color = count > MAX_PARTS ? '#b00020' : '#1a7742';
          } else if (result.error) {
            partCountEl.textContent = result.error;
            partCountEl.style.color = '#b00020';
          }
        };
        reader.onerror = function () {
          partCountEl.textContent = 'Could not preview file';
          partCountEl.style.color = '#b00020';
        };
        reader.readAsText(file);
      }
    }

    fileInfoEl.style.display = 'flex';
    if (dropzone) dropzone.style.display = 'none';
  }

  function clearPendingFile() {
    pendingFile = null;
    pendingFileType = null;
    if (fileInfoEl) fileInfoEl.style.display = 'none';
    if (dropzone) dropzone.style.display = '';
    if (partCountEl) partCountEl.textContent = '';
  }

  // ═══════════════════════════════════════════════════════════════════
  //  INITIALISATION
  // ═══════════════════════════════════════════════════════════════════

  document.addEventListener('DOMContentLoaded', function () {
    var section = document.getElementById('multi-part-search-section');

    if (section) {
      // ── Homepage context ──
      isHomepage = true;
      searchForm   = section.querySelector('#mps-form');
      searchInput  = section.querySelector('#mps-text-input');
      fileInput    = section.querySelector('#mps-csv-input');
      dropzone     = section.querySelector('#mps-csv-dropzone');
      fileInfoEl   = section.querySelector('#mps-csv-file-info');
      filenameEl   = section.querySelector('#mps-csv-filename');
      removeBtn    = section.querySelector('#mps-csv-remove');
      partCountEl  = section.querySelector('#mps-csv-part-count');
      loadingEl    = section.querySelector('#multi-part-loading');
      summaryEl    = section.querySelector('#multi-part-summary');
      resultsEl    = section.querySelector('#multi-part-results');
    } else {
      // ── Search page context ──
      isHomepage = false;
      searchInput = document.getElementById('Search-In-Template');
      if (!searchInput) return;

      searchForm = searchInput.closest('form');
      if (!searchForm) return;

      gridContainer = document.getElementById('ProductGridContainer');
      loadingEl     = document.getElementById('multi-part-loading');
      summaryEl     = document.getElementById('multi-part-summary');
      resultsEl     = document.getElementById('multi-part-results');
    }

    // Cache the loading text <span> for dynamic updates
    if (loadingEl) {
      loadingText = loadingEl.querySelector('span');
    }

    if (searchForm) {
      searchForm.addEventListener('submit', interceptForm);
    }

    if (fileInput) {
      initFileUploadHandlers();
    }
  });
})();
